import { overviewEn } from './areas/overview';
import { setupEn } from './areas/setup';
import { siteEn } from './areas/site';
import { catalogEn } from './areas/catalog';
import { connectionsEn } from './areas/connections';
import { activityEn } from './areas/activity';
import { featuresEn } from './areas/features';
import { builderEn } from './areas/builder';
import { loginsEn } from './areas/logins';
/**
 * **The source of truth.** Every other catalog is checked against this one by
 * the compiler (`es.ts` declares `: Messages`), so this file decides what keys
 * exist and what parameters each of them takes.
 *
 * ## What belongs here, and what does not
 *
 * Here: **words this console writes** — tab labels, column headings, the
 * sentence a confirm dialog quotes before a cutover, the note that says why a
 * button is missing. That is docs/i18n-design.md §2's Domain A, and all of it.
 *
 * Not here, and on this surface the boundary is load-bearing rather than tidy:
 *
 *  - **Server sentences.** `admin-api` writes real ones — "products,
 *    certificates still depend on sales", "Two keys are already active … revoke
 *    one first" — and puts the instruction in `detail.hint`. `bits.tsx`'s
 *    `Failure` prints `message`, `hint` and the list verbatim, because
 *    *"paraphrasing them here would produce a console that is vaguer than the
 *    CLI"*. A key here that restated one would be a second wording of a refusal
 *    we do not own, and the two would drift in the direction of the vaguer one.
 *    What **is** translated is the *framing* around them: the "Not on this
 *    surface — by design" heading and the rule paragraph beside it.
 *  - **The shared vocabulary.** Module slugs and their labels, scope names and
 *    what they grant, `tenant.status`, `authority`, `plan`, job states, delivery
 *    statuses, domain statuses: these come from `@yourstore/domain`'s `MODULES`
 *    and `SCOPES`, or straight off the wire, and they are the same tokens the
 *    CLI prints and the agreement names. An operator reading `partner_api` in
 *    one place and *API de socios* in another would have two vocabularies for
 *    one system.
 *  - **Tenant content.** Names, hostnames, branding slot values, publish notes.
 *    Those are the tenant's words and are printed as written.
 *
 * ## Two conventions this catalog uses
 *
 * **Inline markup lives in the sentence.** A console sentence is full of
 * identifiers — `` `admin_api` ``, `` `pending_dns` `` — and splitting one into
 * "the part before the code" and "the part after" would freeze English word
 * order into the catalog. So an entry may carry two markers, and `bits.tsx`'s
 * `Rich` turns them into elements: `` `like this` `` becomes `<code>`, and
 * `**like this**` becomes `<strong>`. The sentence stays one entry, and a
 * translation may move the identifier wherever its own grammar wants it.
 *
 * **A sentence broken in two means a *link* is in the middle** — the one thing
 * `Rich` cannot produce, because an anchor needs an href. There is exactly one
 * of those (`wizard.catalog.owner.*`), and it says so where it is.
 */

export const en = {
  /* The redesign's areas, one file each (./areas/*). */
  ...overviewEn,
  ...setupEn,
  ...siteEn,
  ...catalogEn,
  ...connectionsEn,
  ...activityEn,
  ...featuresEn,
  ...builderEn,
  ...loginsEn,
  /* ---------------------------------------------------------------- */
  /* Words more than one screen uses                                   */
  /* ---------------------------------------------------------------- */

  'common.cancel': 'Cancel',
  'common.continue': 'Continue',
  'common.back': 'Back',
  'common.backToFleet': '← Businesses',
  'common.refresh': 'Refresh',
  'common.remove': 'remove',
  'common.hide': 'Hide',
  'common.never': 'never',
  'common.working': 'Working…',
  'common.saving': 'Saving…',
  'common.starting': 'Starting…',
  'common.issuing': 'Issuing…',
  'common.applying': 'Applying…',
  'common.creating': 'Creating…',
  'common.name': 'Name',
  'common.status': 'Status',
  'common.integration': 'Integration',
  'common.tenant': 'Business',
  /** English and Spanish spell the plan word identically; see `messages.test.ts`. */
  'common.plan': 'Plan',
  /** An acronym on a field label, the same three letters in both languages. */
  'common.url': 'URL',
  'common.when': 'When',
  'common.actor': 'Actor',
  'common.action': 'Action',
  'common.target': 'Target',

  /** Loading is a word, not a spinner: an operator read is one round trip. */
  'common.reading': ({ what }: { what: string }) => `Reading ${what}…`,
  'loading.fleet': 'the fleet',
  'loading.tenant': 'the business',
  'loading.domains': 'the business’s website addresses',
  'loading.theme': 'the business’s website design',
  'loading.deliveries': 'the delivery log',
  'loading.executions': 'executions',
  'loading.execution': 'the execution',

  /* ---------------------------------------------------------------- */
  /* The shell                                                         */
  /* ---------------------------------------------------------------- */

  'app.seat': 'operator',
  'app.nav.label': 'Console',
  'app.tab.fleet': 'Businesses',
  'app.tab.audit': 'Log',
  'app.tab.jobs': 'Checks & imports',
  'app.devToken': 'dev token',
  'app.devToken.title': 'Unsigned development token',
  'app.signOut': 'Sign out',
  'app.footer': 'Everything done in this console — looking included — is recorded in the log.',

  /* ---------------------------------------------------------------- */
  /* Sign in                                                           */
  /* ---------------------------------------------------------------- */

  'signin.title': 'clearDay operator',
  'signin.sub':
    'For JSWaves operators. Sign in with an operator account; a business’s own staff sign-in does not open the console.',
  'signin.dev':
    'Development sign-in. Any email works and the token is unsigned — this build talks only to an `admin-api` started with `AUTH_FAKE_TOKENS=1`, which refuses to run in production.',
  'signin.email': 'Email',
  'signin.password': 'Password',
  'signin.submit': 'Sign in',
  'signin.busy': 'Signing in…',
  'signin.unavailable': 'Could not reach the sign-in service. Check the connection.',
  'signin.rejected': 'That email and password did not match.',

  /* ---------------------------------------------------------------- */
  /* The small pieces (`bits.tsx`)                                     */
  /* ---------------------------------------------------------------- */

  /**
   * The framing around a `501`. The server's own sentence is printed beside it,
   * untranslated — this is the part that says what the rule is.
   */
  'failure.byDesign.title': 'Not on this surface — by design',
  'failure.byDesign.rule':
    'The operator console never browses a business’s own records — their bookings, clients and money. This is that lock working, not a fault to report.',

  /**
   * **The three sentences no server wrote.**
   *
   * Everything else a `Failure` prints comes off the wire and is printed
   * verbatim — that is the rule above, and it is why a Spanish console shows an
   * English refusal. These three are the exception that proves it: the fetch
   * never landed, the thing in front of `admin-api` answered with something
   * that is not one of our error bodies, or the body did not match the schema.
   * The console is the only one with anything to say, so it says it here, in
   * the operator's language, like every other word it writes.
   */
  'failure.offline': 'Could not reach admin-api.',
  /** Cloud Armor or IAP answering instead of the service. The number is the fact. */
  'failure.http': ({ status }: { status: number }) => `HTTP ${String(status)}.`,
  'failure.malformed': 'admin-api sent a shape this console does not know.',

  'once.note':
    'This is the only time the key is shown. Only its SHA-256 hash is stored, so it cannot be recovered — if it is lost, revoke it and issue another.',
  'once.copy': 'Copy',
  'once.copied': 'Copied',
  'once.stored': 'I have stored it',

  /* ---------------------------------------------------------------- */
  /* Fleet                                                             */
  /* ---------------------------------------------------------------- */

  'fleet.filter.any': 'any',
  'fleet.revoked': ({ count }: { count: number }) => ` (+${String(count)} revoked)`,

  'pager.previous': 'Previous',
  'pager.next': 'Next',
  'pager.range': ({ from, to }: { from: string; to: string }) => `${from}–${to}`,
  'pager.rangeOf': ({ from, to, total }: { from: string; to: string; total: string }) =>
    `${from}–${to} of ${total}`,

  /* ---------------------------------------------------------------- */
  /* The tenant page                                                   */
  /* ---------------------------------------------------------------- */

  'tenant.audit': 'Log for this business',
  'tenant.jobs': 'Checks & imports',

  /* "See what she sees" — the read-only support look (status §5; X5). */
  'support.note': ({ name }: { name: string }) =>
    `Open ${name}’s staff app read-only for 15 minutes. Every look is recorded in the log; nothing can be changed through it.`,
  'support.button': 'See what they see',
  'support.opening': 'Opening…',
  'support.opened': ({ until, auditId }: { until: string; auditId: string }) =>
    `Opened in a new tab — read-only until ${until}, recorded as ${auditId}.`,
  /**
   * Lifecycle vocabulary (spec §5.7.1, §4.4) — what each status means, said
   * plainly. These lived in `vocabulary.ts` until Domain A; they are console
   * copy, and the status *token* they are keyed by is not.
   */
  'status.meaning.onboarding': 'Being set up. The partner is not connected yet.',
  'status.meaning.importing':
    'Running alongside their old system while their book moves in — partners are told availability is approximate.',
  'status.meaning.live':
    'We hold their book. This is the only stage in which partners are told availability is exact.',
  'status.meaning.paused': 'Not answering for now: partners can neither see availability nor book.',
  'status.meaning.offboarded':
    'Finished with us. Nothing can be booked and no client details are shared.',

  /* ---------------------------------------------------------------- */
  /* The offer simulator (docs/lovespa-gaps-plan.md A2–A4)             */
  /* ---------------------------------------------------------------- */

  'sim.title': 'Offer simulator',
  'sim.note':
    'Type a day out of the owner’s book and read what each policy would offer for it. Nothing here is read from or written to their book — the engine runs in this browser on what you type.',
  'sim.paperNote':
    'The minimum lead time and the same-day cut-off are in the form below and deliberately **not** in the columns: they decide *when* we will answer, the columns decide *which* times we name, and an empty column should never mean “you set a two-week lead”.',
  'sim.date': 'The day',
  'sim.openFrom': 'Open from',
  'sim.openTo': 'Open until',
  'sim.grid': 'Start every (minutes)',
  'sim.gap': 'Gap between guests (minutes)',
  'sim.gapHint':
    'The house default. An item that carries its own buffer keeps it; an item at zero takes this.',
  'sim.ask': 'The ask (minutes)',
  'sim.shortest': 'Shortest treatment (minutes)',
  'sim.shortestHint':
    'Used to work out what counts as a useful gap — the shortest thing on the menu, plus the gap.',
  'sim.bookings': 'What is already in the book',
  'sim.bookingsHint': ({ gap }: { gap: number }) =>
    `Write the treatment’s own length, as the paper book does. The ${String(gap)}-minute gap is added on top.`,
  'sim.bookingStart': 'Starts',
  'sim.bookingLength': 'Minutes',
  'sim.bookingStartLabel': ({ row }: { row: number }) => `Start of booking ${String(row)}`,
  'sim.bookingLengthLabel': ({ row }: { row: number }) => `Length of booking ${String(row)}`,
  'sim.addBooking': 'Add a booking',
  'sim.keptCount': ({ kept }: { kept: number }) =>
    `${String(kept)} time${kept === 1 ? '' : 's'} offered`,
  'sim.relaxed': ({ applied }: { applied: string }) =>
    `This column offered nothing, so the answer came from the rule that ${applied} instead.`,
  'sim.nothing': 'No free start at all on this day.',
  'sim.lead': 'lead',
  'sim.leadsWith': ({ times }: { times: string }) =>
    times === '' ? 'Nothing to lead with.' : `An assistant would lead with ${times}.`,
  'sim.choose': 'Use this one',
  'sim.chosen': 'Chosen',
  'sim.reason.pool': 'A pooled resource — the policy does not apply to it.',
  'sim.reason.sliverBefore': ({ minutes }: { minutes: number }) =>
    `leaves ${String(minutes)} minutes free before it`,
  'sim.reason.sliverAfter': ({ minutes }: { minutes: number }) =>
    `leaves ${String(minutes)} minutes free after it`,
  'sim.reason.floating': 'floats in the middle — touches nothing',
  'sim.reason.notAnchor': 'not one of the day’s anchors',
  'sim.reason.paced': 'the kitchen cap is already full in that step',
  'sim.reason.taken': 'the room is already promised at that time',
  'sim.reason.overCap': 'past the number of times one answer carries',
  'sim.policyTitle': 'The booking rule for this business',
  'sim.policyNote':
    'These five settings decide which times guests and partners are offered. Nothing here touches their book, their hours or their prices, and a member of staff booking by hand is never narrowed by any of it.',
  'sim.rank': 'Order',
  'sim.rankHint':
    'Ordering only — “packing” reads out the ones that close a hole first. Nothing becomes unbookable by being last.',
  'sim.usefulGap': 'Useful gap (minutes)',
  'sim.usefulGapHint': 'Leave empty to work it out from the menu.',
  'sim.anchors': 'Anchors on an empty day',
  'sim.anchorsHint':
    'Local times, comma separated. Empty means the opening and the latest start that fits.',
  'sim.topPartner': 'Lead with (agent)',
  'sim.topGuest': 'Lead with (guest site)',
  'sim.topHint': 'How many to read out first. The rest stay bookable.',
  'sim.bufferBefore': 'Gap before (minutes)',
  'sim.bufferAfter': 'Gap after (minutes)',
  'sim.bufferAfterHint': '“I do a half an hour between clients.”',
  'sim.minLead': 'Minimum notice (minutes)',
  'sim.leadHint': 'How far ahead a stranger may book. Zero is no floor beyond “not in the past”.',
  'sim.cutoff': 'Same-day cut-off',
  'sim.cutoffHint': 'After this local time, today stops being offered at all.',
  'sim.protectedTitle': 'Hours kept back',
  'sim.protectedNote':
    'Never offered to a guest or an agent; the owner can still put somebody in them. A lunch break, say.',
  'sim.protectedDayLabel': ({ row, day }: { row: number; day: string }) =>
    `${day} on kept-back hours ${String(row)}`,
  'sim.protectedFromLabel': ({ row }: { row: number }) => `Kept-back hours ${String(row)}, from`,
  'sim.protectedToLabel': ({ row }: { row: number }) => `Kept-back hours ${String(row)}, until`,
  'sim.addProtected': 'Keep some hours back',
  'sim.copy': 'Copy the settings',
  'sim.copied': 'Copied',
  'sim.apply': 'Save as their booking rule',
  'sim.applied': ({ mode, gap, lead }: { mode: string; gap: number; lead: number }) =>
    `Saved. The booking rule ${mode}, with a ${String(gap)}-minute gap and ${String(lead)} minutes of notice.`,

  /* ---------------------------------------------------------------- */
  /* The counter's own six answers (B4, C1, §2.3, spec §3.6)           */
  /* ---------------------------------------------------------------- */

  /**
   * Six questions in the words an owner would use, and that is the whole test:
   * somebody who has never read the spec should be able to change one of these
   * and know what they have changed. The tokens underneath — `at_pickup`,
   * `asap`, `request` — stay in the CLI and the audit trail, where the audience
   * is a person reading a log; here they are sentences, because the audience is
   * a person sitting next to the owner.
   */
  'settings.saved': ({
    tenders,
    payment,
    booking,
  }: {
    tenders: string;
    payment: string;
    booking: string;
  }) =>
    `Saved. The till shows ${tenders}; collections are paid ${payment}; the website: ${booking}`,

  'settings.tenderLabel': ({ tender }: { tender: string }) => `Show the ${tender} button`,
  'settings.noTenders': 'Leave at least one button on, or there is nothing to press at the till.',

  'settings.pickup': 'Can guests collect in person?',
  'settings.pickupHint':
    'Reserve online, collect at the counter. Turning this on is the moment a stranger can take something off the shelf before anybody has been paid.',
  'settings.pickup.yes': 'Yes — they can reserve and collect',
  'settings.pickup.no': 'No — nothing is collected in person',
  'settings.ship': 'Do we post things out?',
  'settings.shipHint':
    'The posted shop: a live postage quote and a pay link. This is what every shop in the fleet does unless somebody says otherwise.',
  'settings.ship.yes': 'Yes — we post orders out',
  'settings.ship.no': 'No — nothing is posted',

  'settings.payment': 'When do they pay?',
  'settings.paymentHint':
    'Paying when they reserve records what is owed but sends nobody anywhere to pay it yet — somebody still has to ask. Left alone, this follows whether the business can take money online at all.',
  'settings.payment.atPickup': 'At the counter, when it changes hands',
  'settings.payment.prepay': 'When they reserve it',

  'settings.expiry': 'How long we hold a collection (days)',
  'settings.expiryHint':
    'Counted from the day it is marked ready. It is read once, when something becomes ready, so changing it moves nothing that is already waiting on the shelf.',

  'settings.timing': 'When takeaway orders are for',
  'settings.timingHint':
    'A counter that fries to order cannot promise a time; a caterer cannot work without one. The server enforces whichever is chosen, so a guest is never asked a question the business has already answered.',
  'settings.timing.asap': 'As soon as it is ready — no times anywhere',
  'settings.timing.scheduled': 'For a time they choose — always',
  'settings.timing.either': 'Either: as soon as possible, or scheduled',

  /*
   * The kitchen's own controls (docs/verticals-plan.md V1 / R4). Two knobs,
   * one block: how a ticket gets confirmed, and how long the kitchen says.
   */
  'settings.confirmation': 'How does the kitchen confirm an order?',
  'settings.confirmationHint':
    'A person confirms each ticket from the pass and says how long, or every ticket is confirmed the moment it lands with the default prep time. Nothing is texted to a guest until a ticket is confirmed with a time.',
  'settings.confirmation.staff': 'A person confirms each ticket',
  'settings.confirmation.auto': 'Confirmed the moment it is placed',
  'settings.prep': 'Default prep time (minutes)',
  'settings.prepHint':
    'What the Confirm button is prefilled with, and what an automatic confirmation writes. Leave it empty for no default — then a ticket confirmed without a time texts nothing. Zero means “ready now” and is texted.',
  'settings.prepNone': 'none',

  'settings.booking': 'Does the website book, or ask?',
  'settings.bookingHint':
    'Their own site only. An agent on the phone and a member of staff at the desk are never narrowed by this, and a guest can always read availability, see their own visits and cancel one.',
  'settings.booking.open': 'Anyone may book themselves',
  'settings.booking.trusted': 'Regulars book; everybody else asks',
  'settings.booking.request': 'Everybody asks, and the business confirms',

  /**
   * A5's companion knob. It is drawn only under `trusted` and `request`, and
   * the hint says the one thing an operator will otherwise get wrong: changing
   * it does not shorten a hold somebody has already been promised.
   */
  'settings.hold': 'How long does a request hold its slot?',
  'settings.holdHint':
    'Hours, 1 to 168. An unanswered request keeps its time this long and then lapses, and the guest is told. Changing this does not move a request that is already waiting — its deadline is set when the guest asks.',

  /* ---------------------------------------------------------------- */
  /* Keys                                                              */
  /* ---------------------------------------------------------------- */

  'keys.issue': 'Issue key',

  /* ---------------------------------------------------------------- */
  /* Webhook endpoints                                                 */
  /* ---------------------------------------------------------------- */

  'webhooks.secretRef': 'Secret reference',

  /* ---------------------------------------------------------------- */
  /* Domains (spec §6.4.2)                                             */
  /* ---------------------------------------------------------------- */

  'domains.yes': 'yes',
  /** Spanish spells it the same; see the exception list in `messages.test.ts`. */
  'domains.no': 'no',

  /* ---------------------------------------------------------------- */
  /* Jobs                                                              */
  /* ---------------------------------------------------------------- */

  'jobs.title': 'Checks & imports',
  'jobs.navLabel': 'Job',
  'jobs.tab.conformance': 'Conformance',
  'jobs.tab.import': 'Import',
  'jobs.unconfigured.title': 'This job is not configured in this environment',
  'jobs.unconfigured.rule':
    'The variable is optional on purpose: a console deployed before its Cloud Run Jobs exist is still the console you would use to finish creating them. Nothing is broken — there is nothing to trigger yet.',

  'jobs.conformance.title': 'Run the conformance suite',
  'jobs.conformance.note':
    'Checks every promise of the partner agreement against a business’s real book. It expects the prepared test businesses — a run against a business nobody prepared fails and means nothing.',
  'jobs.baseUrl': 'Base URL override',
  'jobs.baseUrlPlaceholder': "leave empty to use the job's configured PROVIDER_BASE_URL",
  'jobs.run': 'Run the partner-contract check',
  'jobs.started': ({ tenantId, detail }: { tenantId: string; detail: string }) =>
    `Started against ${tenantId}. ${detail}`,
  'jobs.execution': ({ executionId }: { executionId: string }) => `Execution ${executionId}.`,

  'jobs.import.title': 'Import a LoveSpa database',
  'jobs.import.note':
    'Dry run first, always. A dry run reads the old app’s file and reports what it would write; only an execute writes anything, and totals that match to the cent are what allow the switch-over.',
  'jobs.import.startedDry': ({ tenantId, detail }: { tenantId: string; detail: string }) =>
    `Dry run started against ${tenantId}. ${detail}`,
  'jobs.import.startedExecute': ({ tenantId, detail }: { tenantId: string; detail: string }) =>
    `EXECUTING import started against ${tenantId}. ${detail}`,
  'jobs.import.file': 'File path',
  'jobs.import.fileHint':
    'Wherever the job can reach it. Nothing is uploaded through this console.',
  'jobs.import.mode': 'Mode',
  'jobs.import.dryRun':
    '**Dry run** — read the file and report. Writes nothing. This is the default and it is the default on purpose.',
  'jobs.import.execute':
    '**Execute** — write the history into this business. Run a dry run first and read its totals; this cannot be undone from the console.',
  'jobs.import.startDry': 'Start dry run',
  'jobs.import.startExecute': 'Execute import',
  'jobs.import.confirmTitle': ({ tenantId }: { tenantId: string }) =>
    `Execute the import into ${tenantId}?`,
  'jobs.import.confirmConsequence':
    'This writes a year of history into the business for real. The switch-over waits on a dry run whose totals match to the cent — if you have not read one for this file, cancel and run a dry run instead.',
  'jobs.import.confirmLabel': 'Execute the import',

  'jobs.executions.title': 'Recent executions',
  'jobs.executions.note':
    'State is derived from the task counters, not read from a status field — an execution with no completion time and a name is running; one that completed with a failed task is failed.',
  'jobs.executions.empty': 'Nothing has run yet.',
  'jobs.th.execution': 'Execution',
  'jobs.th.state': 'State',
  'jobs.th.started': 'Started',
  'jobs.th.completed': 'Completed',
  'jobs.th.tasks': 'Tasks',
  'jobs.running': 'running',
  'jobs.tasks': ({ succeeded, total }: { succeeded: number; total: number }) =>
    `${String(succeeded)}/${String(total)}`,
  'jobs.failedFlag': ({ count }: { count: number }) => `${String(count)} failed`,
  'jobs.detail': 'Detail',
  'jobs.dt.resource': 'Resource',
  'jobs.dt.created': 'Created',
  'jobs.logsElsewhere':
    'The output is not here. A job’s logs are in Google Cloud’s logs under this run’s id — an import’s row-by-row output is the business’s own data, and this console never holds any.',
  'jobs.picker.choose': 'choose a business…',
  'jobs.picker.failed': ({ message }: { message: string }) =>
    `Could not list the businesses: ${message}`,
  'jobs.state.running': 'running',
  'jobs.state.succeeded': 'succeeded',
  'jobs.state.failed': 'failed',
  'jobs.state.unknown': 'unknown',

  /* ---------------------------------------------------------------- */
  /* The onboarding wizard                                             */
  /* ---------------------------------------------------------------- */

  'wizard.title': 'Add a business',
  'wizard.openTenant': 'Open the business page',
  'wizard.step.create': 'Create the business',
  'wizard.step.modules': 'Turn on features',
  'wizard.step.key': 'Issue the partner key',
  'wizard.step.webhook': 'Send booking updates to the partner',
  'wizard.step.catalog': 'Load what they sell',
  'wizard.step.conformance': 'Run the partner-contract check',

  'wizard.create.title': 'Create the business',
  'wizard.create.note':
    'You choose the id, and it is never reused. The business starts in Setting up: partners are told its availability is approximate until you move it to Live, which is the switch-over and not a step here.',
  'wizard.create.tenantId': 'Business id',
  'wizard.create.tenantIdHint':
    '`t_`, then lower-case letters, digits and underscores — `t_lovespa_frenchtown`.',
  'wizard.create.timezone': 'Timezone',
  'wizard.create.timezoneHint':
    'Full IANA name. Availability is computed in it — a wrong one is a booking an hour off.',
  'wizard.create.currency': 'Currency',
  'wizard.create.submit': 'Create the business',

  'wizard.modules.title': 'Turn on features',
  'wizard.modules.note':
    'What a feature needs is checked against everything this asks for at once, so the whole partner path goes in one step. Ask for booking partners without the calendar and the server says what is missing — that answer is shown here rather than guessed at.',
  'wizard.modules.legend': 'Features',
  'wizard.modules.needs': ({ list }: { list: string }) => ` — needs ${list}`,
  'wizard.modules.enabled': ({ list }: { list: string }) => `Enabled: ${list}`,
  'wizard.modules.submit': 'Enable and continue',

  'wizard.key.title': 'Issue the partner key',
  'wizard.key.note':
    'A test key with what the concierge needs — it cannot change client details, and reaches nothing in products, checkout, gift cards, account credit, payments or reports.',
  'wizard.key.scopes': ({ scopes }: { scopes: string }) =>
    `It may: ${scopes}. It is a test key, and says so itself, so nobody has to guess which one they are looking at.`,

  'wizard.webhook.title': 'Send booking updates to the partner',
  'wizard.webhook.note':
    'Where booking updates go. https only; the secret reference is the name of the secret in Google Cloud, never the secret itself — it is looked up each time an update is sent.',
  'wizard.webhook.created': ({ url }: { url: string }) => `Created → ${url}`,
  'wizard.webhook.updated': ({ url }: { url: string }) => `Updated → ${url}`,
  'wizard.webhook.skip': 'Skip for now',
  'wizard.webhook.submit': 'Save and continue',

  'wizard.catalog.title': 'Load what they sell',
  'wizard.catalog.note': 'Three places can do this now. The wizard is not one of them.',
  'wizard.catalog.heading':
    'A business is not bookable until it has services, the people and rooms that do them, and hours',
  'wizard.catalog.body':
    'This step used to say the console could not do it and would not learn to. It has learned: the What they sell tab on the business page loads a catalog file and edits what comes out, because a real client with a photo shoot in the diary should not need somebody at a terminal. What has not changed is the fence underneath it — prices and lengths can be changed only while the business is being set up, and their book, their guests and their money are as out of reach as they ever were.',
  'wizard.catalog.rule': ({ tenantId }: { tenantId: string }) =>
    `This step still asks rather than checks, and that is now a choice about **where** rather ` +
    `than about permission: loading \`${tenantId}\`’s catalog is a file somebody reads and a dry ` +
    `run somebody looks at, which belongs on the business page beside the catalog it produces — ` +
    `not behind a Next button.`,
  'wizard.catalog.two': 'Three places can do it, and every one of them is audited:',
  /**
   * The one sentence in this catalog that is two entries, because an **anchor**
   * sits in the middle of it and `Rich` produces `<code>` and `<strong>` only —
   * an `<a>` needs an href, which is a prop and not a marker. The two halves are
   * the text on either side of the staff-app link.
   */
  'wizard.catalog.owner.before':
    '**The owner, in their own staff app** — Settings → services, resources and hours, signed in as themselves at ',
  'wizard.catalog.owner.after':
    '. This is the path that scales, because it is the person who knows the menu.',
  'wizard.catalog.cli': ({ tenantId }: { tenantId: string }) =>
    `**The command line** — \`pnpm --filter @yourstore/cli cli -- catalog seed --tenant ${tenantId} …\`, ` +
    `for a move from another system, a demo business, or anything a file describes better than a form.`,
  'wizard.catalog.console':
    '**What they sell, on the business page** — paste their file, dry-run it, load it, then dress what comes out: descriptions, headings, order and their own photographs.',
  'wizard.catalog.confirm': ({ tenantId }: { tenantId: string }) =>
    `The catalog for \`${tenantId}\` is loaded. I have checked it on the business page, in ` +
    `the staff app or on the command line.`,

  'wizard.conformance.title': 'Run the partner-contract check',
  'wizard.conformance.note':
    'Checks every promise of the partner agreement against this business. Green here means a partner can rely on it.',
  'wizard.conformance.notLive': ({ tenantId, status }: { tenantId: string; status: string }) =>
    `${tenantId} is in ${status}, so partners are told its availability is approximate, and the ` +
    `checks that need a live business will not pass. Moving it to Live is the switch-over — it is ` +
    `done from the business page, deliberately not as a click at the end of this path.`,
  'wizard.conformance.failedTasks': ({ count }: { count: number }) => ` — ${String(count)} failed`,
  'wizard.conformance.green': ({ tenantId }: { tenantId: string }) =>
    `Green. ${tenantId} answers every obligation the agreement makes — it can take partner bookings.`,
  'wizard.conformance.red':
    'Red. The failing checks are in this execution’s logs in Cloud Logging; fix and run again.',
  'wizard.conformance.again': 'Run again',
  'wizard.conformance.finish': 'Finish — open the business page',

  /* ---------------------------------------------------------------- */
  /* Branding — the theme editor (spec §6.4.3)                         */
  /* ---------------------------------------------------------------- */

  'branding.publish': 'Publish',
  'branding.published': ({
    version,
    bytes,
    effective,
  }: {
    version: number;
    bytes: number;
    effective: string;
  }) => `Published version ${String(version)} (${String(bytes)} bytes). ${effective}`,
  'branding.publishNotePlaceholder': 'why this change',

  'branding.historyEmpty': ({ kept }: { kept: number }) =>
    `No earlier version is kept yet. The last ${String(kept)} are kept; anything older comes back ` +
    `from a backup, not with a button.`,

  'branding.useTemplate': 'Use this template',
  'branding.templateTitle': ({ label }: { label: string }) => `Start from ${label}?`,
  'branding.templateChosen': ({ label }: { label: string }) => `Started from ${label}.`,
  'branding.shopping': 'Photographs to bring',
  'branding.shoppingHint':
    'Every placeholder the template shows, with the shape it needs. Paste each picture’s https: address into the block it names.',
  'branding.shoppingItem': ({
    count,
    ratio,
    where,
  }: {
    count: number;
    ratio: string;
    where: string;
  }) => (count === 0 ? `${where} — ${ratio}` : `${String(count)} × ${ratio} for ${where}`),

  'branding.siteTitle': 'Site title',
  'branding.tagline': 'Tagline',

  'branding.tokensChanged': ({ count }: { count: number }) => `${String(count)} changed`,
  'branding.colourPicker': ({ label }: { label: string }) => `${label} colour picker`,

  'branding.motion': 'Motion',

  /* ---------------------------------------------------------------- */
  /* The content page — the fourth page shape (§9.6 decision 5)        */
  /* ---------------------------------------------------------------- */

  'branding.pages': 'Pages',
  'branding.pagesHint': ({ max }: { max: number }) =>
    `A standalone page of words at its own address — the story, the questions, the policies, ` +
    `the occasions. Up to ${String(max)}, each arranged out of the same blocks the home page ` +
    `is, and each reachable from the nav bar below as \`page:<address>\`.`,
  'branding.pagesNone':
    'No content page yet. The FAQ, the story and the occasions page belong here rather than as bands on the home page, which is what the block list above no longer offers them for.',
  'branding.pageAddress': ({ slug }: { slug: string }) => `/p/${slug}`,
  'branding.pageTitle': 'Title',
  'branding.pageTitleHint': 'The heading, the browser tab, and the word the nav shows.',
  'branding.pageSlug': 'Address',
  'branding.pageSlugPlaceholder': 'faq',
  'branding.pageTitlePlaceholder': 'Questions',
  'branding.addPage': 'Add page',
  'branding.pageLinked': 'in the nav',
  'branding.pageUnlinked': 'not in the nav',
  'branding.removePage': ({ slug }: { slug: string }) => `Remove /p/${slug}`,
  'branding.removePageTitle': ({ slug }: { slug: string }) => `Remove the page /p/${slug}?`,
  'branding.removePageConsequence': ({ slug }: { slug: string }) =>
    `The page and everything arranged on it leave this draft. Nothing is published until you ` +
    `publish, and the version that still has /p/${slug} stays in the history either way.`,
  'branding.removePageLinked': ({ label }: { label: string }) =>
    `The nav entry “${label}” points at it: remove that link too, or point it somewhere else, ` +
    `or the publish is refused.`,

  /* The slug box's refusals, while the operator types. The rules are the
     served ones (`content_pages`), and the server still owns the refusal. */
  'branding.slugTaken': ({ slug }: { slug: string }) =>
    `This document already has a page at /p/${slug}.`,
  'branding.slugLong': ({ max }: { max: number }) =>
    `An address is at most ${String(max)} characters — it is a thing a person reads out.`,
  'branding.slugShape':
    'An address is lower-case letters, digits and single hyphens between them: `what-to-bring`, not `What To Bring`.',
  'branding.slugReserved': ({ slug }: { slug: string }) =>
    `\`${slug}\` is an address a route already owns, so a page written there would be stored and ` +
    `never once shown to a guest.`,
  'branding.pagesFull': ({ max }: { max: number }) =>
    `${String(max)} content pages is the ceiling. Every one of them rides in the single branding ` +
    `row guest-api reads in full on every page load.`,

  /* The localized fields — a title and a nav label are tenant content. */
  'branding.inLanguage': ({ label, language }: { label: string; language: string }) =>
    `${label} (${language})`,
  'branding.addLanguage': ({ label, language }: { label: string; language: string }) =>
    `Write the ${label.toLowerCase()} in ${language}`,
  'branding.dropLanguage': ({ label, language }: { label: string; language: string }) =>
    `Remove the ${language} ${label.toLowerCase()}`,
  'branding.plusLanguage': ({ language }: { language: string }) => `+ ${language}`,

  /* ---------------------------------------------------------------- */
  /* The nav bar                                                       */
  /* ---------------------------------------------------------------- */

  'branding.navHint':
    'The bar every page wears. It is internal-only by grammar — there is no shape a URL can go in — and a link to a content page is `page:<address>`. A target of another shape (a home-page anchor, a category, a built-in view) has no editor here yet and is kept exactly as it is stored.',
  'branding.navNone': 'No nav bar configured. The site renders its minimal shell.',
  'branding.navLabel': ({ n }: { n: number }) => `Link ${String(n)} label`,
  'branding.navTargetLabel': 'Goes to',
  'branding.navTarget': ({ n }: { n: number }) => `Link ${String(n)} target`,
  'branding.navRemove': ({ n }: { n: number }) => `Remove link ${String(n)}`,
  'branding.navLink': ({ title }: { title: string }) => `Link ${title} from the nav`,
  /**
   * The server's own sentence, said sooner. A bar entry left pointing at a
   * removed page is a link to the bundle's not-found treatment, shipped by an
   * operator who believed they had made a page — so the publish is refused on
   * both sides rather than on one.
   */
  'branding.navUndeclared': ({ target }: { target: string }) =>
    `\`${target}\` — nav points at a page this document does not declare.`,
  'branding.navUndeclaredHint':
    'Add the page back, or point the link somewhere else. The publish is refused either way — this is the same refusal the server makes, made before the request instead of after it.',

  'branding.survivedUnchanged': 'Published as written — the sanitizer changed nothing.',
  'branding.survived': ({
    removed,
    before,
    after,
  }: {
    removed: string;
    before: number;
    after: number;
  }) =>
    `Sanitized on publish: ${removed} removed (${String(before)} → ${String(after)} bytes). ` +
    `What is stored, and what every guest gets, is:`,

  'branding.rowLabel': ({ label, n }: { label: string; n: number }) =>
    `${label} ${String(n)} label`,
  'branding.rowValue': ({ label, n }: { label: string; n: number }) =>
    `${label} ${String(n)} value`,
  'branding.addRow': 'Add row',
  /*
   * GS2 — the badges on a `policies` row. The first is the owner's ruling 2
   * drawn on the screen: the engine's rule wins and the row is flagged, not
   * silently dropped. The second is a hint from a regex over prose and says so
   * in its own wording ("check", never "wrong").
   */
  'branding.rowKind': ({ label, n }: { label: string; n: number }) => `${label} ${String(n)} kind`,
  'branding.policyKind.none': '— none',
  'branding.policyKind.cancellation': 'Cancellation',
  'branding.policyKind.lead_time': 'Booking ahead',
  'branding.policyKind.request_hold': 'Requests',
  'branding.policyKind.pickup_expiry': 'Pickup',
  'branding.policyKind.deposit': 'Deposit',
  'branding.policyKind.other': 'Other',
  'branding.policyOverridden': ({ rule }: { rule: string }) =>
    `Not in effect — the engine's rule is shown instead: ${rule}`,
  'branding.policySuspected': ({ rules }: { rules: string }) =>
    `Looks like a rule the engine keeps — check this against the engine's own: ${rules}`,
  'branding.policyRule.lead_time': ({ minutes }: { minutes: number }) =>
    `guests book at least ${String(minutes)} min ahead`,
  'branding.policyRule.same_day_cutoff': ({ time }: { time: string }) =>
    `same-day bookings close at ${time}`,
  'branding.policyRule.request_hold': ({ hours }: { hours: number }) =>
    `a request holds its time for ${String(hours)} h`,
  'branding.policyRule.pickup_expiry': ({ days }: { days: number }) =>
    `a ready order waits on the shelf ${String(days)} ${days === 1 ? 'day' : 'days'}`,
  'branding.policyRule.deposit_percent': ({
    percent,
    minCents,
  }: {
    percent: number;
    minCents: number;
  }) => `a ${String(percent)} % deposit, at least ${(minCents / 100).toFixed(2)}`,
  'branding.policyRule.deposit_fixed': ({ cents }: { cents: number }) =>
    `a fixed deposit of ${(cents / 100).toFixed(2)}`,
  'branding.policyRule.house_gap': ({ minutes }: { minutes: number }) =>
    `${String(minutes)} min kept between appointments`,
  'branding.policyRule.waiver': ({ title }: { title: string }) => `a waiver is signed: ${title}`,
  'branding.stringLabel': ({ label, n }: { label: string; n: number }) => `${label} ${String(n)}`,

  'branding.css': 'Custom stylesheet',
  'branding.cssHint':
    'The only styling the business writes itself. Class and element selectors, `@media` if you need it — no `@import`, no `url()`, no ids. The server refuses the rest rather than editing it: a stylesheet is written by somebody who meant every character.',
  /** An acronym on a field label, the same three letters in both languages. */
  'branding.cssBytes': ({ bytes, limit }: { bytes: number; limit: number }) =>
    `${String(bytes)} / ${String(limit)} bytes`,
  'branding.cssPinned': ({ version }: { version: number }) =>
    ` · the published sheet was written for version ${String(version)} of the website’s theme; ` +
    `check it again when the theme changes.`,
  'branding.cssNotYet':
    'The website does not use this yet — it is stored and checked now, ready for when it does.',

  'branding.sanitizer': 'What a content zone may contain',
  'branding.sanitized': ({ count }: { count: number }) =>
    `${String(count)} sanitized on last publish`,
  'branding.allowed': 'Allowed:',
  /** The list arrives unjoined, because "or" is a word and `,` is not. */
  'branding.linkSchemes': ({ schemes }: { schemes: readonly string[] }) =>
    `Links may be ${schemes.join(' or ')} only.`,

  /* ---------------------------------------------------------------- */
  /* The concierge integration                                         */
  /*                                                                   */
  /* Two vocabularies meet on this card and only one of them is ours.  */
  /* The *tokens* — `balanced`, `ai_first`, `provider_connection` —     */
  /* are aiconcierge's and are printed as they arrive, for the reason  */
  /* module slugs are: an operator reading `balanced` here and         */
  /* `equilibrado` in the other console would hold two words for one   */
  /* setting. What is translated is the sentence *beside* each token — */
  /* the plain-language meaning their §5 table gives — which is        */
  /* exactly Domain A of docs/i18n-design.md §2.                       */
  /* ---------------------------------------------------------------- */

  'concierge.note':
    'The AI that answers the telephone and the chat. What the agent **knows** — hours, prices, policies, questions — is the business’s and is edited where it already lives; what the agent **does** is set here.',
  'concierge.notConfigured': 'Concierge integration is not configured on this deployment.',
  'concierge.notConfiguredVars': ({ vars }: { vars: string }) =>
    `Missing: ${vars}. Nothing is wrong with this business — this deployment simply has no partner to talk to.`,

  'concierge.enable': 'Enable concierge',
  'concierge.enabling': 'Enabling…',
  'concierge.enabled': ({ prefix }: { prefix: string }) =>
    `Enabled. A key (${prefix}…) and a signing secret went machine to machine — neither ever reached this browser.`,

  'concierge.wizard.title': 'The concierge',
  'concierge.wizard.note':
    'One action: the features it needs turned on, an access key made, a signing secret stored, the business added on the concierge’s side, and booking updates pointed back here. The key and the secret never reach this browser — that is the whole reason this step replaced the copy-paste.',
  'concierge.wizard.other':
    'Any other partner keeps the old path: a key shown once, then the address for booking updates typed in.',

  /* ---------------------------------------------------------------- */
  /* The tenant intake (docs/tenant-intake-design.md)                  */
  /* ---------------------------------------------------------------- */

  'loading.intake': 'the owner’s answers',
  'loading.photos': 'the photographs',

  'intake.notSentNote':
    'Pick the shape of their page and the language they read, and the link is on the next screen. It is the only copy — send it, do not save it.',

  'intake.template': 'Template',
  'intake.templateHint':
    'The shape of their page. It decides which questions they are asked, and you can change your mind later.',
  'intake.language': 'Language',
  'intake.languageHint': 'The language the form opens in. They may answer in another.',

  'intake.mint': 'Make the link',
  'intake.remint': 'Make a new link',
  'intake.remintConsequence':
    'The link they already have stops working the moment this is made — if they have it open on their phone, it will stop saving. Their answers are kept. Send them the new one.',
  'intake.linkLabel': ({ host }: { host: string }) => `The owner’s link · ${host}`,
  'intake.messageLabel': 'The message to send',
  'intake.copyMessage': 'Copy the message',
  /**
   * Two entries rather than one translated one, and this is the single place on
   * this surface where the **owner's** language decides rather than the
   * operator's: the operator reads an English console and the message goes to
   * somebody who was asked to fill the form in in Spanish.
   */
  'intake.message.en': ({ link }: { link: string }) =>
    `Hi — this is the form for your new page. It takes about 20 minutes, saves as you go, and you can add photos from your phone: ${link}`,
  'intake.message.es': ({ link }: { link: string }) =>
    `Hola — este es el formulario para tu nueva página. Toma unos 20 minutos, se guarda solo mientras escribes y puedes añadir fotos desde el móvil: ${link}`,

  'intake.start.warnings': 'Worth an eye before you start',
  'intake.start.unplaced':
    'These answers are not on the page. Paste them where you want them, or shorten them first.',
  'intake.start.done': ({ template }: { template: string }) =>
    `The editor below is now a draft of ${template} with the owner’s words in it. Nothing is published yet.`,

  /* ---- the catalog (GS4, migration 0050) ------------------------- */

  'intake.menu.seed': 'Seed this',
  'catalog.notGranted':
    'This deployment has not yet been updated to let the console change what a business sells. Until it is, a catalog is loaded from the operator command line, and so are photographs.',
  'catalog.tab.scheduled': 'Services',
  'catalog.tab.stocked': 'Shop',
  'catalog.tab.prepared': 'Menu',
  'catalog.tab.stay': 'Rooms',
  'catalog.tab.departure': 'Sailings',
  'catalog.tab.other': 'Everything else',
  'catalog.vertical.pool': ({ id }: { id: string }) => `Pool ${id}`,
  'catalog.vertical.vessel': ({ id }: { id: string }) => `Vessel ${id}`,
  'catalog.vertical.sleeps': ({ n }: { n: number }) => `Sleeps ${String(n)}`,
  'catalog.vertical.nightly': ({ price }: { price: string }) => `From ${price} a night`,
  'catalog.vertical.calendar': ({ bands, exceptions }: { bands: number; exceptions: number }) =>
    `${String(bands)} season band(s), ${String(exceptions)} priced night(s)`,
  'catalog.vertical.minNights': ({ n }: { n: number }) => `${String(n)}-night minimum`,
  'catalog.vertical.checkTimes': ({ inAt, outAt }: { inAt: string; outAt: string }) =>
    `Check-in ${inAt}, check-out ${outAt}`,
  'catalog.vertical.seats': ({ n }: { n: number }) => `${String(n)} seats`,
  'catalog.vertical.charter': ({ n }: { n: number }) => `Private charter, ${String(n)} seats`,
  'catalog.vertical.length': ({ min }: { min: number }) => `${String(min)} min`,
  'catalog.vertical.starts': ({ n }: { n: number }) => `${String(n)} start(s) a week`,
  'catalog.vertical.season': ({ from, to }: { from: string; to: string }) =>
    `Season ${from} to ${to}`,
  'catalog.vertical.yearRound': 'Year-round',
  'catalog.vertical.tiers': 'Ticket types',
  'catalog.vertical.soldOut': '86’d',
  'catalog.vertical.offPartner': 'Off the AI agent’s board',
  'catalog.vertical.offSite': 'Off the website',
  'catalog.name': 'Name',
  'catalog.nameOf': ({ item }: { item: string }) => `Name of ${item}`,
  'catalog.price': 'Price',
  'catalog.priceOf': ({ item }: { item: string }) => `Price of ${item}`,
  'catalog.duration': 'Minutes',
  'catalog.durationOf': ({ item }: { item: string }) => `Duration of ${item}`,
  /* The gap either side of a treatment (0064). Blank = the house's; 0 = none. */
  'catalog.bufferBefore': 'Gap before',
  'catalog.bufferBeforeOf': ({ item }: { item: string }) => `Gap before ${item}`,
  'catalog.bufferAfter': 'Gap after',
  'catalog.bufferAfterOf': ({ item }: { item: string }) => `Gap after ${item}`,
  'catalog.houseDefault': ({ minutes }: { minutes: number | null }) =>
    minutes === null ? 'house default' : `house default (${String(minutes)})`,
  'catalog.bufferHint': ({ minutes }: { minutes: number | null }) =>
    minutes === null
      ? 'Minutes. Blank takes the house default; 0 means none.'
      : `Minutes. Blank takes the house default (${String(minutes)}); 0 means none.`,
  'catalog.category': 'Heading',
  'catalog.categoryOf': ({ item }: { item: string }) => `Heading of ${item}`,
  'catalog.sortOrder': 'Position',
  'catalog.sortOrderHint': 'Lower comes first. Ties fall back to the id.',
  'catalog.sortOrderOf': ({ item }: { item: string }) => `Position of ${item}`,
  'catalog.brand': 'Brand',
  'catalog.brandHint': 'Who makes it — printed on the product card.',
  'catalog.brandOf': ({ item }: { item: string }) => `Brand of ${item}`,
  'catalog.descriptionHint': 'The sentence a guest reads before booking it.',
  'catalog.descriptionOf': ({ item }: { item: string }) => `Blurb of ${item}`,
  /* Options and add-ons (migration 0048, W5c). */
  'catalog.options': 'Ways this one is sold',
  'catalog.options.headline': 'Follows the marked length below — the server keeps them in step.',
  'catalog.options.name': 'Called',
  'catalog.options.nameOf': ({ item, ref }: { item: string; ref: string }) =>
    `What the ${ref} length of ${item} is called`,
  /* The second-language box beside the name (L8). Blank sends nothing new. */
  'catalog.options.nameIn': ({ language }: { language: string }) => `Also in ${language}`,
  'catalog.options.nameInOf': ({
    item,
    ref,
    language,
  }: {
    item: string;
    ref: string;
    language: string;
  }) => `What the ${ref} length of ${item} is called in ${language}`,
  'catalog.options.newNameIn': ({ item, language }: { item: string; language: string }) =>
    `What a new length on ${item} is called in ${language}`,
  'catalog.options.language.en': 'English',
  'catalog.options.language.es': 'Spanish',
  'catalog.options.price': 'Price',
  'catalog.options.priceOf': ({ item, ref }: { item: string; ref: string }) =>
    `Price of the ${ref} length of ${item}`,
  'catalog.options.minutes': 'Minutes',
  'catalog.options.minutesHint': 'Leave blank if this one does not change how long it takes.',
  'catalog.options.minutesOf': ({ item, ref }: { item: string; ref: string }) =>
    `Minutes of the ${ref} length of ${item}`,
  'catalog.options.default': 'The one quoted',
  'catalog.options.defaultOf': ({ item, ref }: { item: string; ref: string }) =>
    `Quote the ${ref} length of ${item} by default`,
  'catalog.options.active': 'Still sold',
  'catalog.options.activeOf': ({ item, ref }: { item: string; ref: string }) =>
    `Whether the ${ref} length of ${item} is still sold`,
  'catalog.options.up': ({ item, ref }: { item: string; ref: string }) =>
    `Move the ${ref} length of ${item} earlier`,
  'catalog.options.down': ({ item, ref }: { item: string; ref: string }) =>
    `Move the ${ref} length of ${item} later`,
  'catalog.options.save': 'Save this length',
  'catalog.options.saveOf': ({ item, ref }: { item: string; ref: string }) =>
    `Save the ${ref} length of ${item}`,
  'catalog.options.add': 'Add a length',
  'catalog.options.addNote':
    'Prices are what the whole thing costs, never an extra on top — an owner writes 85, 170, 255, not 85 and two “plus 85”s. There is no delete: a length somebody was sold still has to appear on last March’s report, so retire it instead.',
  'catalog.options.ref': 'Short key',
  /** The sentence before the server's 409: this item already has that key. */
  'catalog.options.refTaken': ({ ref }: { ref: string }) =>
    `This one already has a length keyed ${ref} — pick another key, or edit that one above.`,
  'catalog.options.newRef': ({ item }: { item: string }) => `Short key for a new length on ${item}`,
  'catalog.options.newName': ({ item }: { item: string }) =>
    `What a new length on ${item} is called`,
  'catalog.options.newPrice': ({ item }: { item: string }) => `Price of a new length on ${item}`,
  'catalog.options.newMinutes': ({ item }: { item: string }) =>
    `Minutes of a new length on ${item}`,
  'catalog.options.create': 'Add this length',
  'catalog.options.createOf': ({ item }: { item: string }) => `Add a length to ${item}`,
  'catalog.addOns': 'What may be added to it',
  'catalog.addOns.empty': 'Nothing may be added to this one yet.',
  'catalog.addOns.paused': 'paused',
  'catalog.addOns.unlink': 'Unlink',
  'catalog.addOns.unlinkOf': ({ item, name }: { item: string; name: string }) =>
    `Stop offering ${name} with ${item}`,
  'catalog.addOns.pickHint': 'It stays a service of its own; this only says it may ride along.',
  'catalog.addOns.price': 'Price attached',
  'catalog.addOns.priceOf': ({ item }: { item: string }) => `Price attached to ${item}`,
  'catalog.addOns.priceHint': 'Blank means the same as it costs on its own.',
  'catalog.addOns.minutes': 'Minutes attached',
  'catalog.addOns.minutesOf': ({ item }: { item: string }) => `Minutes attached to ${item}`,
  'catalog.addOns.minutesHint': 'Blank means its own length. 0 is real — it adds no time.',
  'catalog.addOns.link': 'Link it',
  'catalog.addOns.linkOf': ({ item }: { item: string }) => `Link an add-on to ${item}`,
  'catalog.save': 'Save',
  'catalog.saved': 'Saved.',
  'catalog.photosTitle': 'Photographs',
  'catalog.photosEmpty': 'No photographs on this one yet.',
  'catalog.alt': 'What it shows',
  'catalog.altOf': ({ position }: { position: number }) =>
    `What photograph ${String(position)} shows`,
  'catalog.altRequired': 'Every photograph needs a line saying what it shows before this saves.',
  'catalog.moveUp': ({ position }: { position: number }) =>
    `Move photograph ${String(position)} earlier`,
  'catalog.moveDown': ({ position }: { position: number }) =>
    `Move photograph ${String(position)} later`,
  'catalog.removePhoto': ({ position }: { position: number }) =>
    `Remove photograph ${String(position)}`,
  'catalog.remove': 'Remove',
  'catalog.forThisItem': 'Taken of this one',
  'catalog.pick': 'Choose…',
  'catalog.pasteUrl': 'Or paste a picture’s address',
  'catalog.addPhoto': 'Add',
  'catalog.savePhotos': 'Save photographs',
  'catalog.photosSaved': 'Photographs saved, in this order.',
  'catalog.categories': 'Headings',
  'catalog.categoriesNote':
    'A heading is a label on each thing rather than a page of its own, so renaming one changes every thing filed under it — which is the only way to do it without ending up with two pages differing by a comma.',
  'catalog.renameFrom': 'Rename',
  'catalog.renameTo': 'To',
  'catalog.renameToHint': 'Leave blank to take them out of any heading.',
  'catalog.rename': 'Rename everywhere',
  'catalog.renamed': ({ items, to }: { items: number; to: string }) =>
    `${String(items)} thing(s) now filed under “${to}”.`,
  'catalog.add': 'Add one thing',
  'catalog.addNote':
    'A service or a shelf product. A dish, a room type or a departure needs a catalog file rather than a form — paste one below.',
  'catalog.kind': 'What kind',
  'catalog.newName': 'Name of the new thing',
  'catalog.newPrice': 'Price of the new thing',
  'catalog.newDuration': 'Minutes for the new thing',
  'catalog.create': 'Add it',
  'catalog.seed': 'Seed from a catalog file',
  'catalog.seedNote':
    'The same document the operator CLI reads, through the same reader. Dry run first — it runs the real thing and rolls it back, so what it tells you is what would happen.',
  'catalog.seedFile': 'The file',
  'catalog.dryRun': 'Dry run',
  'catalog.seedThis': 'Seed this',
  'catalog.seedDryRun': ({
    services,
    resources,
    stays,
    departures,
    version,
  }: {
    services: number;
    resources: number;
    stays: number;
    departures: number;
    version: number;
  }) =>
    `Would seed ${String(services)} thing(s), ${String(resources)} room(s), ${String(stays)} room type(s) and ${String(departures)} sailing(s), leaving the catalog at version ${String(version)}. Nothing was written.`,
  'catalog.seedApplied': ({
    services,
    resources,
    stays,
    departures,
    version,
  }: {
    services: number;
    resources: number;
    stays: number;
    departures: number;
    version: number;
  }) =>
    `Seeded ${String(services)} thing(s), ${String(resources)} room(s), ${String(stays)} room type(s) and ${String(departures)} sailing(s). Catalog is now version ${String(version)}.`,

  'wizard.step.intake': 'Ask the owner',
  'wizard.intake.title': 'Ask the owner to fill the page in',
  'wizard.intake.note':
    'One link, twenty minutes on their phone, and the words and photographs for their page come back written by the person who knows the business. Optional — you can fill the page in by hand as before.',
  'wizard.intake.skip': 'Skip — I will write the page myself',
  'wizard.intake.continue': 'Continue',

  /* ---------------------------------------------------------------- */
  /* The business page (console redesign, phase A)                     */
  /* ---------------------------------------------------------------- */

  /*
   * Neutral pronouns throughout: the console cannot know how an owner is
   * referred to, so it says "they", "the owner" or the business's name
   * (docs/console-redesign-plan.md §2) — even where the mockup, written about
   * one owner, says "she".
   */
  'business.tabs.label': 'Business',
  'business.tab.overview': 'Overview',
  'business.tab.setup': 'Setup',
  'business.tab.site': 'Website',
  'business.tab.catalog': 'What they sell',
  'business.tab.connections': 'Connections',
  'business.tab.activity': 'Activity',
  'business.actions.label': 'Business actions',
  'business.protected': 'Protected — cannot be wiped',
  'business.protected.title':
    'This business holds real data. Nothing can delete it until protection is removed from the command line, which leaves its own record.',
  'business.details.title': 'Business details',

  /**
   * The stages an operator sees (plan §2, "Stages"). The status *token* stays
   * the wire's; these are the words for it. "Taking bookings online" is a
   * milestone on the track, not a status anybody sets.
   */
  'stage.track.label': 'Getting live',
  'stage.track.step': ({ n }: { n: number }) => `Stage ${String(n)}`,
  'stage.track.later': 'Later',
  'stage.track.now': 'now',
  'stage.name.onboarding': 'Setting up',
  'stage.name.importing': 'Moving their book in',
  'stage.name.live': 'Live',
  'stage.name.paused': 'Paused',
  'stage.name.offboarded': 'Offboarded',
  'stage.name.online': 'Taking bookings online',
  'stage.hint.onboarding': 'services, hours, their login',
  'stage.hint.importing': 'clients and appointments',
  'stage.hint.live': 'they run their day here',
  'stage.hint.online': 'website, texts, concierge',

  'stage.menu': 'Change stage',
  'stage.option.onboarding': 'Back to setting up',
  'stage.option.importing': 'Start moving their book in',
  'stage.option.live': 'Go live',
  'stage.option.paused': 'Pause',
  'stage.option.offboarded': 'Offboard…',
  /** One line under each option: what the stage means for the business and its partners. */
  'stage.meaning.onboarding': 'Partners are told times are approximate while setup finishes.',
  'stage.meaning.importing':
    'Bookings come in from their old system while they keep using it. Partners are told times are approximate.',
  'stage.meaning.live': 'Their book here is the real one. Partners are told times are exact.',
  'stage.meaning.paused': 'Nobody can book. Nothing is lost.',
  'stage.meaning.offboarded': 'Finished with us. Nothing can be booked; the id is never reused.',

  /**
   * The confirm for each move. The consequence is what changes **for the
   * business and for whoever is booking with it**, within seconds — the plain
   * rewrite of what used to be `status.consequence.*`, which spoke in
   * `authority` and `capabilities`. It stays as grave as that did: the live one
   * is still the cutover.
   */
  'stage.confirm.title.onboarding': ({ name }: { name: string }) =>
    `Move ${name} back to setting up?`,
  'stage.confirm.title.importing': ({ name }: { name: string }) =>
    `Start moving ${name}’s book in?`,
  'stage.confirm.title.live': ({ name }: { name: string }) => `Take ${name} live?`,
  'stage.confirm.title.paused': ({ name }: { name: string }) => `Pause ${name}?`,
  'stage.confirm.title.offboarded': ({ name }: { name: string }) => `Offboard ${name}?`,
  'stage.consequence.onboarding': ({ name }: { name: string }) =>
    `Partners stop being told ${name}’s times are exact. An assistant talking to a client right now will start saying its times are approximate.`,
  'stage.consequence.importing': ({ name }: { name: string }) =>
    `${name}’s clients and appointments can be brought in while they keep working in their old system. Partners are told times are approximate until they go live, because the old system can still take a booking.`,
  'stage.consequence.live': ({ name }: { name: string }) =>
    `This is the switch-over. From now on ${name}’s book here is the real one: partners are told times are exact and will promise them to clients. Do this only after the switch-over hour with the owner.`,
  'stage.consequence.paused': ({ name }: { name: string }) =>
    `Nobody can book with ${name} — in the app, online or through a partner. Partners work around them instead of failing mid-conversation. Nothing is lost, and you can resume at any time.`,
  'stage.consequence.offboarded': ({ name }: { name: string }) =>
    `${name} is finished with us. Nothing can be booked, partners are told there is nothing to book, and the id is never used again. Their data is not deleted.`,
  'stage.confirm.onboarding': 'Move back',
  'stage.confirm.importing': 'Start',
  'stage.confirm.live': 'Go live',
  'stage.confirm.paused': 'Pause',
  'stage.confirm.offboarded': 'Offboard',
  /**
   * The honest number is repeated rather than rounded away: the key directory is
   * cached in memory for 15 seconds, so a move is not instant (spec §5.2).
   */
  'stage.moved': ({ name, stage, seconds }: { name: string; stage: string; seconds: number }) =>
    `${name} is now ${stage}. Partners see the change within ${String(seconds)} seconds.`,
  'stage.toast': ({ name, stage }: { name: string; stage: string }) => `${name}: ${stage}`,

  /* Checks & imports, opened from one business. */
  'jobs.executions.scopeLabel': 'Whose runs',
  'jobs.executions.scope.tenant': ({ tenantId }: { tenantId: string }) => `Only ${tenantId}`,
  'jobs.executions.scope.all': 'Every business',
  'jobs.executions.scopeNote':
    'Cloud Run does not record which business a run was for, so these are matched against the runs this business’s own log says were started for it.',
  'jobs.executions.scopeEmpty': 'No run in this list was started for this business.',
};

/**
 * The shape every catalog must have — the reason a missing Spanish key is a
 * build failure rather than an English sentence on a Spanish console.
 *
 * Deliberately **not** `as const`: with literal types every Spanish string would
 * have to equal its English original, which is the opposite of the point. What
 * is pinned is the *set of keys* and the *parameter object* each function takes,
 * which is exactly the part a translation must not change.
 */
export type Messages = typeof en;
